import React, { createContext, useContext, useReducer, ReactNode } from "react";
import { Exercise, Workout } from "../schemas/workout";
import { cloneDeep } from "lodash";
import { generateId } from "../utils/id";
import { createDefaultExercise } from "../utils/factories";

// Define the state shape
interface WorkoutState {
  workout: Workout | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string;
  isDirty: boolean;
  editingField: {
    exerciseId: string;
    field: string;
  } | null;
}

// Define action types
type WorkoutAction =
  | { type: "SET_WORKOUT"; payload: Workout }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_SAVING"; payload: boolean }
  | { type: "SET_ERROR"; payload: string }
  | { type: "SET_DIRTY"; payload: boolean }
  | { type: "UPDATE_WORKOUT_NAME"; payload: string }
  | { type: "UPDATE_WORKOUT_DESCRIPTION"; payload: string }
  | { type: "ADD_EXERCISE" }
  | { type: "UPDATE_EXERCISE"; payload: Exercise }
  | { type: "DELETE_EXERCISE"; payload: string }
  | { type: "DUPLICATE_EXERCISE"; payload: string }
  | { type: "START_EDITING"; payload: { exerciseId: string; field: string } }
  | { type: "STOP_EDITING" }
  | {
      type: "UPDATE_EXERCISE_TYPE";
      payload: { exerciseId: string; type: Exercise["structure"]["type"] };
    }
  | {
      type: "UPDATE_EXERCISE_DETAIL";
      payload: { exerciseId: string; field: string; value: string };
    }
  | { type: "REORDER_EXERCISES"; payload: Exercise[] };

// Define the context value shape
interface WorkoutContextValue extends WorkoutState {
  dispatch: React.Dispatch<WorkoutAction>;
}

// Create the context
const WorkoutContext = createContext<WorkoutContextValue | undefined>(
  undefined
);

// Initial state
const initialState: WorkoutState = {
  workout: null,
  isLoading: true,
  isSaving: false,
  error: "",
  isDirty: false,
  editingField: null,
};

// Reducer function
function workoutReducer(
  state: WorkoutState,
  action: WorkoutAction
): WorkoutState {
  switch (action.type) {
    case "SET_WORKOUT":
      return {
        ...state,
        workout: action.payload,
        isLoading: false,
      };
    case "SET_LOADING":
      return {
        ...state,
        isLoading: action.payload,
      };
    case "SET_SAVING":
      return {
        ...state,
        isSaving: action.payload,
      };
    case "SET_ERROR":
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };
    case "SET_DIRTY":
      return {
        ...state,
        isDirty: action.payload,
      };
    case "UPDATE_WORKOUT_NAME":
      if (!state.workout) return state;
      return {
        ...state,
        workout: { ...state.workout, name: action.payload },
        isDirty: true,
      };
    case "UPDATE_WORKOUT_DESCRIPTION":
      if (!state.workout) return state;
      return {
        ...state,
        workout: { ...state.workout, description: action.payload },
        isDirty: true,
      };
    case "ADD_EXERCISE": {
      if (!state.workout) return state;
      const newExercise = createDefaultExercise();
      return {
        ...state,
        workout: {
          ...state.workout,
          exercises: [...state.workout.exercises, newExercise],
        },
        isDirty: true,
      };
    }
    case "UPDATE_EXERCISE": {
      if (!state.workout) return state;
      const updatedExercises = state.workout.exercises.map((ex) =>
        ex.id === action.payload.id ? action.payload : ex
      );
      return {
        ...state,
        workout: { ...state.workout, exercises: updatedExercises },
        isDirty: true,
      };
    }
    case "DELETE_EXERCISE": {
      if (!state.workout) return state;
      const filteredExercises = state.workout.exercises.filter(
        (ex) => ex.id !== action.payload
      );
      return {
        ...state,
        workout: { ...state.workout, exercises: filteredExercises },
        isDirty: true,
      };
    }
    case "DUPLICATE_EXERCISE": {
      if (!state.workout) return state;
      const exerciseToDuplicate = state.workout.exercises.find(
        (ex) => ex.id === action.payload
      );
      if (!exerciseToDuplicate) return state;

      const duplicate = {
        ...cloneDeep(exerciseToDuplicate),
        id: generateId(),
        name: `${exerciseToDuplicate.name} (Copy)`,
      };

      const index = state.workout.exercises.findIndex(
        (ex) => ex.id === action.payload
      );
      const exercisesWithDuplicate = [...state.workout.exercises];
      exercisesWithDuplicate.splice(index + 1, 0, duplicate);

      return {
        ...state,
        workout: { ...state.workout, exercises: exercisesWithDuplicate },
        isDirty: true,
      };
    }
    case "START_EDITING": {
      return {
        ...state,
        editingField: action.payload,
      };
    }
    case "STOP_EDITING": {
      return {
        ...state,
        editingField: null,
      };
    }
    case "UPDATE_EXERCISE_TYPE": {
      if (!state.workout) return state;
      const exercisesWithUpdatedType = state.workout.exercises.map((ex) =>
        ex.id === action.payload.exerciseId
          ? {
              ...ex,
              structure: {
                ...ex.structure,
                type: action.payload.type,
                // Add default values based on type
                ...(action.payload.type === "sets" && { sets: 3, reps: 10 }),
                ...(action.payload.type === "timed" && {
                  sets: 1,
                  duration: 60,
                }),
                ...(action.payload.type === "interval" && {
                  sets: 1,
                  rounds: 5,
                  work: 30,
                  rest: 15,
                }),
                ...(action.payload.type === "cadence" && {
                  sets: 3,
                  reps: 10,
                  tempo: "3-1-3",
                }),
              },
            }
          : ex
      );
      return {
        ...state,
        workout: { ...state.workout, exercises: exercisesWithUpdatedType },
        isDirty: true,
      };
    }
    case "UPDATE_EXERCISE_DETAIL": {
      if (!state.workout) return state;
      const exercise = state.workout.exercises.find(
        (ex) => ex.id === action.payload.exerciseId
      );
      if (!exercise) return state;

      const updatedExercise = cloneDeep(exercise);
      const numValue = parseInt(action.payload.value, 10) || 0;

      if (action.payload.field === "rest_time") {
        updatedExercise.rest_time = numValue;
      } else {
        const structureType = exercise.structure.type;
        switch (structureType) {
          case "sets": {
            if (action.payload.field === "sets") {
              updatedExercise.structure.sets = numValue || 1;
            } else if (action.payload.field === "reps") {
              updatedExercise.structure.reps = numValue || 1;
            }

            break;
          }
          case "timed": {
            if (action.payload.field === "sets") {
              updatedExercise.structure.sets = numValue || 1;
            } else if (action.payload.field === "duration") {
              updatedExercise.structure.duration = numValue || 1;
            }
            break;
          }
          case "interval": {
            if (action.payload.field === "sets") {
              updatedExercise.structure.sets = numValue || 1;
            } else if (action.payload.field === "rounds") {
              updatedExercise.structure.rounds = numValue || 1;
            } else if (action.payload.field === "work") {
              updatedExercise.structure.work = numValue || 1;
            } else if (action.payload.field === "rest") {
              updatedExercise.structure.rest = numValue || 1;
            }
            break;
          }
          case "cadence": {
            if (action.payload.field === "sets") {
              updatedExercise.structure.sets = numValue || 1;
            } else if (action.payload.field === "reps") {
              updatedExercise.structure.reps = numValue || 1;
            } else if (action.payload.field === "tempo") {
              updatedExercise.structure.tempo = action.payload.value;
            }
            break;
          }
        }
      }

      const exercisesWithUpdatedDetail = state.workout.exercises.map((ex) =>
        ex.id === updatedExercise.id ? updatedExercise : ex
      );

      return {
        ...state,
        workout: { ...state.workout, exercises: exercisesWithUpdatedDetail },
        isDirty: true,
      };
    }
    case "REORDER_EXERCISES":
      return {
        ...state,
        workout: state.workout
          ? { ...state.workout, exercises: action.payload }
          : state.workout,
        isDirty: true,
      };
    default:
      return state;
  }
}

// Provider component
export function WorkoutProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(workoutReducer, initialState);

  return (
    <WorkoutContext.Provider value={{ ...state, dispatch }}>
      {children}
    </WorkoutContext.Provider>
  );
}

// Custom hook for using the context
export function useWorkout() {
  const context = useContext(WorkoutContext);
  if (context === undefined) {
    throw new Error("useWorkout must be used within a WorkoutProvider");
  }
  return context;
}
